import { useCallback, useEffect, useMemo, useState } from "react";
import { WorldMap } from "../components/WorldMap";
import { MapLegend } from "../components/MapLegend";
import { CountryList } from "../components/CountryList";
import { PurchasePanel } from "./PurchasePanel";
import { useCountryClicks } from "../hooks/useCountryClicks";
import { useCountries } from "../hooks/useCountries";
import { useAppConfig } from "../hooks/useAppConfig";
import { usePaymentProviders } from "../hooks/usePaymentProviders";
import { useHumanSession } from "../hooks/useHumanSession";
import { supabase, callEdgeFunction } from "../lib/supabaseClient";
import type { ClickCounterRow, OrderStatusResult } from "../lib/types";
import { useLanguage } from "../i18n/LanguageContext";

interface FreeClickResponse {
  countryNumeric: number;
  clicks: number;
}

/**
 * Single merged map: every country carries two kinds of votes added
 * together - free clicks (anyone can add one after a one-time Turnstile
 * human check, see useHumanSession) and purchased clicks (financial support
 * via Stripe/PayPal/BuyMeCoffee, see PurchasePanel). Clicking a country on
 * the map or in the ranked list both selects it (revealing its breakdown +
 * the purchase panel below) AND registers a free click for it, exactly like
 * the old free-only map did - buying more support is a separate, explicit
 * action inside PurchasePanel and never requires the Turnstile check (see
 * README, "Zaščita pred zlorabami" - payment flows were deliberately left
 * frictionless).
 *
 * The map's color scale and the ranked list both use the COMBINED total
 * (free + purchased) so a country's position reflects all of its votes
 * regardless of source; PurchasePanel breaks the two back out (plus the EUR
 * total, plus the free/this-year/this-week/today breakdown) once a country
 * is selected.
 */
export function MapPage() {
  const { t } = useLanguage();
  const { countries } = useCountries();
  const { rows: freeRows, loading: freeLoading } = useCountryClicks("free");
  const { rows: premiumRows, loading: premiumLoading } = useCountryClicks("premium");
  const { limits } = useAppConfig();
  const { providers } = usePaymentProviders();
  const { session, verifying, error: verifyError, containerRef, invalidate, retry: retryVerification } = useHumanSession();

  const [selected, setSelected] = useState<{ numeric: number; name: string } | null>(null);
  const [resumeOrderId, setResumeOrderId] = useState<string | undefined>(undefined);
  const [resumeCancelled, setResumeCancelled] = useState(false);
  const [clickPending, setClickPending] = useState(false);
  const [clickFeedback, setClickFeedback] = useState<string | null>(null);

  // Combined totals for the map's color scale and the ranked list: clicks =
  // free + purchased votes added together, amount_cents carried over from
  // the premium side untouched (free clicks never carry an amount).
  const combinedRows = useMemo(() => {
    const combined = new Map<number, ClickCounterRow>();
    const countryNumerics = new Set<number>([...freeRows.keys(), ...premiumRows.keys()]);
    for (const countryNumeric of countryNumerics) {
      const free = freeRows.get(countryNumeric);
      const premium = premiumRows.get(countryNumeric);
      const clicks = (free?.clicks ?? 0) + (premium?.clicks ?? 0);
      if (clicks <= 0) continue;
      const updatedCandidates = [free?.updated_at, premium?.updated_at].filter(
        (value): value is string => Boolean(value),
      );
      updatedCandidates.sort();
      combined.set(countryNumeric, {
        country_numeric: countryNumeric,
        clicks,
        amount_cents: premium?.amount_cents ?? 0,
        updated_at: updatedCandidates[updatedCandidates.length - 1] ?? new Date().toISOString(),
      });
    }
    return combined;
  }, [freeRows, premiumRows]);

  const maxClicks = useMemo(() => {
    let max = 0;
    for (const row of combinedRows.values()) {
      if (row.clicks > max) max = row.clicks;
    }
    return max;
  }, [combinedRows]);

  // The selected country's position in the same combined-total ranking
  // CountryList renders (recomputed here too rather than threaded through
  // as a prop - ~196 rows is cheap to re-sort, and it keeps CountryList
  // self-contained). Feeds the "Share" button's "#3!" text in PurchasePanel;
  // null when the country currently has zero combined clicks (unranked).
  const selectedRank = useMemo(() => {
    if (!selected) return null;
    const ranked = Array.from(combinedRows.values())
      .filter((row) => row.clicks > 0)
      .sort((a, b) => b.clicks - a.clicks);
    const index = ranked.findIndex((row) => row.country_numeric === selected.numeric);
    return index === -1 ? null : index + 1;
  }, [combinedRows, selected]);

  const handleSelect = useCallback(
    async (countryNumeric: number, countryName: string) => {
      setSelected({ numeric: countryNumeric, name: countryName });
      setResumeOrderId(undefined);
      setResumeCancelled(false);
      setClickFeedback(null);

      if (!session) {
        setClickFeedback(t("map.selectFirstFeedback"));
        return;
      }

      setClickPending(true);
      try {
        await callEdgeFunction<FreeClickResponse>("free-click", {
          countryNumeric,
          sessionToken: session.sessionToken,
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : "Error registering click.";
        const lower = message.toLowerCase();
        if (lower.includes("human verification")) {
          invalidate(); // session expired/rejected server-side - re-show the Turnstile widget
          setClickFeedback(t("map.verificationExpired"));
        } else if (lower.includes("too many")) {
          setClickFeedback(t("map.slowDown"));
        } else {
          setClickFeedback(t("map.clickFailed"));
        }
      } finally {
        setClickPending(false);
      }
    },
    [session, invalidate, t],
  );

  // On mount, check whether the visitor was just redirected back from
  // Stripe Checkout (see create-stripe-checkout's success_url/cancel_url)
  // and, if so, resolve which country that order belongs to and select it,
  // so PurchasePanel can resume polling for confirmation instead of the
  // visitor having to re-find the country and start over. This deliberately
  // does NOT register a free click - it's a payment return, not a map click.
  useEffect(() => {
    const url = new URL(window.location.href);
    const orderId = url.searchParams.get("stripe_order_id");
    const cancelledOrderId = url.searchParams.get("stripe_cancelled_order_id");

    if (orderId) {
      (async () => {
        const { data, error } = await supabase.rpc("get_order_status", { p_order_id: orderId }).single();
        if (error) {
          console.error("failed to resolve returning stripe order", error);
          return;
        }
        const result = data as OrderStatusResult;
        const country = countries.get(result.country_numeric);
        if (country) {
          setSelected({ numeric: country.iso_numeric, name: country.name_en });
          setResumeOrderId(orderId);
        }
      })();
    } else if (cancelledOrderId) {
      (async () => {
        const { data, error } = await supabase.rpc("get_order_status", { p_order_id: cancelledOrderId }).single();
        if (error) {
          console.error("failed to resolve cancelled stripe order", error);
          return;
        }
        const result = data as OrderStatusResult;
        const country = countries.get(result.country_numeric);
        if (country) {
          setSelected({ numeric: country.iso_numeric, name: country.name_en });
          setResumeCancelled(true);
        }
      })();
    }

    if (orderId || cancelledOrderId) {
      url.searchParams.delete("stripe_order_id");
      url.searchParams.delete("stripe_cancelled_order_id");
      window.history.replaceState({}, "", url.toString());
    }
    // Runs once countries have loaded, so the country lookup above succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries.size]);

  const selectedFreeClicks = selected ? freeRows.get(selected.numeric)?.clicks ?? 0 : 0;
  const selectedPremiumRow = selected ? premiumRows.get(selected.numeric) : undefined;
  const selectedCountry = selected ? countries.get(selected.numeric) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <p className="rounded-lg bg-slate-100 px-4 py-2.5 text-sm text-slate-600">{t("map.intro")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="flex flex-col gap-3">
          {!session && (
            <div className="flex flex-col items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-sm text-slate-600">
                {verifying ? t("map.verifying") : t("map.confirmNotRobot")}
              </p>
              <div ref={containerRef} />
              {verifyError && (
                <div className="flex flex-col items-start gap-1.5">
                  <p className="text-xs text-red-600">{verifyError}</p>
                  <button
                    type="button"
                    onClick={retryVerification}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t("map.tryAgain")}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="h-[520px]">
            <WorldMap
              countries={countries}
              clicksByCountry={combinedRows}
              maxClicks={maxClicks}
              selectedCountryNumeric={selected?.numeric ?? null}
              onSelectCountry={handleSelect}
            />
          </div>
          <MapLegend />
          <p className="text-sm text-slate-500 min-h-[1.25rem]" role="status">
            {clickPending && t("map.registeringClick")}
            {!clickPending && clickFeedback}
            {!clickPending && !clickFeedback && selected && t("map.selectedCountry", { name: selected.name })}
          </p>

          {selected ? (
            <PurchasePanel
              countryNumeric={selected.numeric}
              countryName={selected.name}
              countryAlpha2={selectedCountry?.iso_alpha2}
              rank={selectedRank}
              currentFreeClicks={selectedFreeClicks}
              currentPremiumClicks={selectedPremiumRow?.clicks ?? 0}
              currentAmountCents={selectedPremiumRow?.amount_cents ?? 0}
              limits={limits}
              providers={providers}
              resumeOrderId={resumeOrderId}
              resumeCancelled={resumeCancelled}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
              {t("map.emptySelection")}
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-slate-800">{t("map.leaderboardHeading")}</h2>
          <CountryList
            countries={countries}
            clicksByCountry={combinedRows}
            maxClicks={maxClicks}
            selectedCountryNumeric={selected?.numeric ?? null}
            onSelectCountry={handleSelect}
          />
        </aside>
      </div>

      {(freeLoading || premiumLoading) && (
        <p className="text-xs text-slate-400" role="status">
          {t("map.loadingData")}
        </p>
      )}
    </div>
  );
}
